import { useRef, useState } from "react";
import { toast } from "sonner";
import { useLang } from "../i18n/LangContext";
import { api, extractError } from "../api/client";
import { compressImage } from "../lib/imageCompression";
import { Camera, Images, CheckCircle2, Loader2, Sparkles } from "lucide-react";

const ACCEPT = "image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif";
const MIME_RE = /^image\/(jpe?g|png|webp|heic|heif)$/i;

// `compress` = preset name ("FOOD" | "PROGRESS" | "EDU") to enable client-side compression, or null to upload as-is.
export function ImageUpload({ endpoint, onUploaded, testId, label, hint, compress = null, thumbnail = false }) {
  const { t } = useLang();
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState(null); // "optimizing" | "uploading"
  const [preview, setPreview] = useState(null);
  const [done, setDone] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!MIME_RE.test(file.type || "")) {
      e.target.value = "";
      return toast.error(t("upload.mimeError"));
    }
    if (file.size > 15 * 1024 * 1024) {
      e.target.value = "";
      return toast.error(t("upload.tooLarge"));
    }

    setBusy(true);
    setDone(false);

    let uploadFile = file;
    let analytics = null;
    if (compress) {
      setStage("optimizing");
      try {
        analytics = await compressImage(file, compress, setStage, { thumbnail });
        uploadFile = analytics.file;
      } catch (err) {
        setBusy(false);
        setStage(null);
        setPreview(null);
        e.target.value = "";
        const code = err?.message;
        const msg =
          code === "too_large" ? t("upload.tooLargeToProcess") : code === "mime_invalid" ? t("upload.mimeError") : t("upload.processError");
        toast.error(msg);
        return; // never upload the original on compression failure
      }
    }

    setPreview(URL.createObjectURL(uploadFile));
    setStage("uploading");
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      if (analytics) {
        fd.append("original_size", String(analytics.originalSize));
        fd.append("img_width", String(analytics.width));
        fd.append("img_height", String(analytics.height));
        if (analytics.thumbnail) fd.append("thumbnail", analytics.thumbnail.file);
      }
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.data.file_id, data.data);
      setDone(true);
    } catch (err) {
      toast.error(extractError(err).message);
      setPreview(null);
      onUploaded(null, null);
    } finally {
      setBusy(false);
      setStage(null);
      e.target.value = "";
    }
  };

  const statusEl = busy ? (
    stage === "optimizing" ? (
      <><Sparkles className="h-4 w-4 animate-pulse text-[#FF3B30]" /> {t("upload.optimizing")}</>
    ) : (
      <><Loader2 className="h-4 w-4 animate-spin" /> {t("upload.uploading")}</>
    )
  ) : done ? (
    <><CheckCircle2 className="h-4 w-4 text-emerald-400" /> {t("upload.done")}</>
  ) : (
    t("upload.choose")
  );

  return (
    <div className="space-y-2">
      {label && <span className="text-slate-300 text-sm block">{label}</span>}
      <input ref={galleryRef} data-testid={testId} type="file" accept={ACCEPT} onChange={pick} className="hidden" />
      <input ref={cameraRef} data-testid={`${testId}-camera`} type="file" accept={ACCEPT} capture="environment" onChange={pick} className="hidden" />
      <div className="rounded-xl border border-dashed border-white/15 bg-[#1C202E] p-4 flex flex-col sm:flex-row items-center gap-4">
        {preview ? <img src={preview} alt="preview" className="h-20 w-20 rounded-lg object-cover shrink-0" />
          : <span className="h-20 w-20 shrink-0 rounded-lg bg-white/5 flex items-center justify-center"><Images className="h-6 w-6 text-slate-400" /></span>}
        <div className="flex-1 w-full space-y-2">
          <div className="text-sm text-white font-medium flex items-center gap-2" data-testid={`${testId}-status`}>{statusEl}</div>
          <div className="flex flex-wrap gap-2">
            <button type="button" data-testid={`${testId}-camera-btn`} onClick={() => cameraRef.current?.click()} disabled={busy}
              className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-slate-200 hover:bg-white/5 inline-flex items-center gap-1.5 disabled:opacity-50">
              <Camera className="h-3.5 w-3.5" /> {t("upload.camera")}
            </button>
            <button type="button" data-testid={`${testId}-gallery-btn`} onClick={() => galleryRef.current?.click()} disabled={busy}
              className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-slate-200 hover:bg-white/5 inline-flex items-center gap-1.5 disabled:opacity-50">
              <Images className="h-3.5 w-3.5" /> {t("upload.gallery")}
            </button>
          </div>
          <div className="text-[11px] text-slate-500">{hint || (compress ? t("upload.hintCompress") : t("upload.hint"))}</div>
        </div>
      </div>
    </div>
  );
}
